import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import {
  Loader2, Save, ArrowLeft, Truck, AlertTriangle, Camera, CheckCircle2, X, Calendar as CalendarIcon,
} from "lucide-react";
import VoiceToTextButton from "@/components/VoiceToTextButton";

const YES_NO = [
  ["si", "SÍ"],
  ["no", "NO"],
];

const INCIDENCIA_NIVEL = [
  ["ninguna", "No, todo correcto"],
  ["leve", "Sí, incidencia leve"],
  ["importante", "Sí, incidencia importante"],
  ["no_usar", "Sí, no usar hasta revisión"],
];

const INCIDENCIA_TIPO = [
  ["limpieza", "Limpieza / orden"],
  ["golpe", "Golpe o daño"],
  ["neumaticos", "Neumáticos / aceite / agua"],
  ["documentacion", "Documentación / ITV / grúa / EPIs"],
  ["averia", "Avería / testigo cuadro"],
  ["otro", "Otro"],
];

const PUEDE_CIRCULAR = [
  ["si", "Sí"],
  ["no", "No"],
  ["no_seguro", "No estoy seguro"],
];

export default function NewVehicleInspection() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const fileRef = useRef(null);

  const [matricula, setMatricula] = useState("");
  const [kilometros, setKilometros] = useState("");
  const [checklist, setChecklist] = useState({
    limpieza_ok: null,
    danos_ok: null,
    neumaticos_ok: null,
    documentacion_ok: null,
  });
  const [incidenciaNivel, setIncidenciaNivel] = useState("");
  const [incidenciaTipo, setIncidenciaTipo] = useState("");
  const [incidenciaDesc, setIncidenciaDesc] = useState("");
  const [puedeCircular, setPuedeCircular] = useState("");
  const [pendingPhotos, setPendingPhotos] = useState([]);
  const [saving, setSaving] = useState(false);
  const [matriculas, setMatriculas] = useState([]);

  // Load previously-used matrículas as autocomplete suggestions AND try to
  // pre-fill from the user's most recent parte de trabajo this week.
  useEffect(() => {
    api.get("/vehicle-inspections/matriculas")
      .then(({ data }) => setMatriculas(data || []))
      .catch(() => {});

    // Monday of current week — LOCAL date (toISOString would shift on DST)
    const today = new Date();
    const dow = today.getDay() || 7; // 1..7 (Sun=7)
    const monday = new Date(today);
    monday.setDate(today.getDate() - (dow - 1));
    const weekStart = `${monday.getFullYear()}-${String(monday.getMonth() + 1).padStart(2, "0")}-${String(monday.getDate()).padStart(2, "0")}`;

    api.get("/work-logs", {
      params: { mine_only: true, fecha_desde: weekStart, limit: 5 },
    })
      .then(({ data }) => {
        const withVehicle = (data || []).find(
          (l) => (l.vehiculo || "").trim().length > 0,
        );
        if (withVehicle) {
          setMatricula((prev) => (prev ? prev : withVehicle.vehiculo.trim().toUpperCase()));
        }
      })
      .catch(() => {});
  }, []);

  // Any No on the 4 checklist Sí/No questions OR incidencia_nivel != ninguna
  // means there's an incident and the detail block is required.
  const anyNo = ["limpieza_ok", "danos_ok", "neumaticos_ok", "documentacion_ok"]
    .some((k) => checklist[k] === false);
  const nivelIsIncident = incidenciaNivel && incidenciaNivel !== "ninguna";
  const hasIncident = anyNo || nivelIsIncident;
  const critical = puedeCircular === "no" || puedeCircular === "no_seguro" || incidenciaNivel === "no_usar";

  const allBasicAnswered =
    matricula.trim() &&
    Number(kilometros) > 0 &&
    ["limpieza_ok", "danos_ok", "neumaticos_ok", "documentacion_ok"].every((k) => checklist[k] !== null) &&
    incidenciaNivel;
  const incidentBlockOk = !hasIncident || (
    incidenciaTipo && incidenciaDesc.trim() && puedeCircular
  );
  const canSubmit = allBasicAnswered && incidentBlockOk && !saving;

  const handlePhotoPick = (e) => {
    const files = Array.from(e.target.files || []).filter((f) => f.type.startsWith("image/"));
    setPendingPhotos((prev) => [...prev, ...files]);
    if (fileRef.current) fileRef.current.value = "";
  };
  const removePendingPhoto = (idx) =>
    setPendingPhotos((prev) => prev.filter((_, i) => i !== idx));

  const submit = async () => {
    if (!canSubmit) return;
    setSaving(true);
    try {
      const payload = {
        matricula: matricula.trim().toUpperCase(),
        kilometros: Number(kilometros),
        limpieza_ok: !!checklist.limpieza_ok,
        danos_ok: !!checklist.danos_ok,
        neumaticos_ok: !!checklist.neumaticos_ok,
        documentacion_ok: !!checklist.documentacion_ok,
        incidencia_nivel: incidenciaNivel,
        incidencia_tipo: hasIncident ? incidenciaTipo : null,
        incidencia_desc: hasIncident ? incidenciaDesc.trim() : null,
        puede_circular: hasIncident ? puedeCircular : null,
      };
      const { data } = await api.post("/vehicle-inspections", payload);
      // Upload any queued photos
      if (pendingPhotos.length) {
        try {
          const fd = new FormData();
          pendingPhotos.forEach((f) => fd.append("files", f));
          await api.post(`/vehicle-inspections/${data.id}/photos`, fd, {
            headers: { "Content-Type": "multipart/form-data" },
          });
        } catch {
          toast.error("Revisión guardada pero no se pudieron subir todas las fotos.");
        }
      }
      // Final message depends on status
      if (data.status === "no_circular") {
        toast.warning(
          "Incidencia crítica registrada. No utilices el vehículo hasta recibir instrucciones.",
          { duration: 8000 }
        );
      } else if (data.status === "incidencia") {
        toast.success("Incidencia registrada. Informa al responsable si aún no lo has hecho.");
      } else {
        toast.success("Revisión registrada correctamente. ¡Gracias!");
      }
      navigate("/", { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo guardar la revisión.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-5 pb-8">
      <button
        onClick={() => navigate(-1)}
        className="inline-flex items-center gap-1 text-slate-500 text-xs font-semibold hover:text-[#FF4500]"
      >
        <ArrowLeft size={14} /> Volver
      </button>
      <header>
        <div className="label-up text-[#FF4500]">Suntelco · Semanal</div>
        <h1 className="font-display font-black text-3xl tracking-tight mt-1 flex items-center gap-2">
          <Truck size={26} /> Revisión de vehículo
        </h1>
        <p className="text-xs text-slate-500 mt-1">
          Si todo está correcto se completa en menos de 1 minuto. Si hay incidencia, se abre el bloque adicional.
        </p>
      </header>

      {/* Sección 1: Datos básicos */}
      <section className="brutal-card p-4 space-y-3">
        <h2 className="label-up">1 · Datos básicos</h2>
        <div>
          <label className="text-xs font-semibold text-slate-600 mb-1 block">
            Nombre del trabajador
          </label>
          <input
            data-testid="vi-nombre"
            className="brutal-input w-full bg-slate-50"
            value={user?.name || ""}
            readOnly
          />
        </div>
        <div>
          <label className="text-xs font-semibold text-slate-600 mb-1 block">Matrícula *</label>
          <input
            data-testid="vi-matricula"
            className="brutal-input w-full uppercase"
            value={matricula}
            onChange={(e) => setMatricula(e.target.value.toUpperCase())}
            placeholder="Ej: 1234 ABC"
            list="matriculas-known"
            autoComplete="off"
          />
          <datalist id="matriculas-known">
            {matriculas.map((m) => <option key={m} value={m} />)}
          </datalist>
        </div>
        <div>
          <label className="text-xs font-semibold text-slate-600 mb-1 block">Kilómetros *</label>
          <input
            data-testid="vi-kilometros"
            type="number"
            min="0"
            className="brutal-input w-full"
            value={kilometros}
            onChange={(e) => setKilometros(e.target.value)}
            placeholder="Ej: 45230"
          />
        </div>
      </section>

      {/* Sección 2: Checklist semanal */}
      <section className="brutal-card p-4 space-y-3">
        <h2 className="label-up">2 · Checklist semanal</h2>
        {[
          // [key, label, inverted]
          // inverted=true means SÍ means "hay problema" (NOT ok, red) and NO
          // means "todo bien" (OK, green). Used for negative-phrased questions.
          ["limpieza_ok", "¿Vehículo limpio y ordenado?", false],
          ["danos_ok", "¿Hay golpes o daños visibles?", true],
          ["neumaticos_ok", "¿Neumáticos, aceite y agua OK?", false],
          ["documentacion_ok", "¿Documentación, ITV, grúa, aceite y cartel de EPIs OK?", false],
        ].map(([key, label, inverted]) => {
          const val = checklist[key];
          const isNotOk = val === false;
          return (
            <div
              key={key}
              className={`rounded-lg border p-3 ${isNotOk ? "bg-rose-50 border-rose-300" : val === true ? "bg-emerald-50/50 border-emerald-200" : "bg-white border-slate-200"}`}
            >
              <p className="text-xs text-slate-700 mb-2">{label}</p>
              <div className="flex gap-2">
                {YES_NO.map(([v, btnLabel]) => {
                  const isYesBtn = v === "si";
                  // Value stored when this button is clicked. With inverted
                  // questions the mapping is flipped: SÍ = not ok, NO = ok.
                  const storeAs = inverted ? !isYesBtn : isYesBtn;
                  const isActive = val === storeAs;
                  // Button is "positive" when clicking it means the check
                  // passes (green). For normal questions that's SÍ; for
                  // inverted questions it's NO.
                  const isPositive = inverted ? !isYesBtn : isYesBtn;
                  return (
                    <button
                      key={v}
                      type="button"
                      onClick={() => setChecklist((c) => ({ ...c, [key]: storeAs }))}
                      data-testid={`vi-${key}-${v}`}
                      className={`flex-1 h-10 rounded-lg text-xs font-bold transition-colors ${
                        isActive
                          ? isPositive ? "bg-emerald-600 text-white" : "bg-rose-600 text-white"
                          : "bg-slate-100 text-slate-500 hover:bg-slate-200"
                      }`}
                    >
                      {btnLabel}
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })}

        {/* Question 8: overall incidence level */}
        <div className="rounded-lg border border-slate-200 p-3 space-y-2">
          <p className="text-xs text-slate-700">¿Hay alguna incidencia que comunicar? *</p>
          <div className="grid grid-cols-1 gap-1.5">
            {INCIDENCIA_NIVEL.map(([v, label]) => (
              <button
                key={v}
                type="button"
                onClick={() => setIncidenciaNivel(v)}
                data-testid={`vi-nivel-${v}`}
                className={`h-10 rounded-lg text-xs font-semibold text-left px-3 border transition-colors ${
                  incidenciaNivel === v
                    ? v === "ninguna"
                      ? "bg-emerald-600 text-white border-emerald-700"
                      : v === "no_usar"
                        ? "bg-rose-700 text-white border-rose-900"
                        : "bg-amber-500 text-white border-amber-700"
                    : "bg-slate-50 text-slate-600 border-slate-200 hover:bg-slate-100"
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Sección 3: Detalle de incidencia (solo si aplica) */}
      {hasIncident && (
        <section
          className="brutal-card p-4 space-y-3 border-2 border-rose-300 bg-rose-50/40"
          data-testid="vi-incident-block"
        >
          <h2 className="label-up text-rose-700 flex items-center gap-1.5">
            <AlertTriangle size={14} /> 3 · Detalle de incidencia
          </h2>
          <div>
            <label className="text-xs font-semibold text-slate-600 mb-1 block">
              ¿Qué ocurre? *
            </label>
            <select
              data-testid="vi-tipo"
              className="brutal-input w-full"
              value={incidenciaTipo}
              onChange={(e) => setIncidenciaTipo(e.target.value)}
            >
              <option value="">— Selecciona —</option>
              {INCIDENCIA_TIPO.map(([v, label]) => (
                <option key={v} value={v}>{label}</option>
              ))}
            </select>
          </div>
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="text-xs font-semibold text-slate-600 block">
                Explica brevemente el problema *
              </label>
              <VoiceToTextButton
                testId="vi-desc-voice"
                onAppend={(text) => setIncidenciaDesc(
                  (prev) => (prev ? prev.replace(/\s+$/, "") + " " : "") + text
                )}
              />
            </div>
            <textarea
              data-testid="vi-desc"
              className="brutal-input w-full min-h-[80px]"
              value={incidenciaDesc}
              onChange={(e) => setIncidenciaDesc(e.target.value)}
              placeholder="Breve y concreto. Puedes escribir o dictar con el micro ↑"
            />
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-600 mb-1 block">
              ¿Puede circular? *
            </label>
            <div className="grid grid-cols-3 gap-2">
              {PUEDE_CIRCULAR.map(([v, label]) => (
                <button
                  key={v}
                  type="button"
                  onClick={() => setPuedeCircular(v)}
                  data-testid={`vi-circular-${v}`}
                  className={`h-10 rounded-lg text-xs font-bold transition-colors ${
                    puedeCircular === v
                      ? v === "si" ? "bg-emerald-600 text-white"
                        : "bg-rose-600 text-white"
                      : "bg-slate-100 text-slate-500 hover:bg-slate-200"
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>

          {/* Photos */}
          <div>
            <label className="text-xs font-semibold text-slate-600 mb-1 block">
              Fotos de la incidencia
            </label>
            <div className="flex flex-wrap gap-2 mb-2">
              {pendingPhotos.map((f, i) => (
                <div key={i} className="relative w-16 h-16 rounded-lg overflow-hidden border border-slate-200">
                  <img src={URL.createObjectURL(f)} alt="" className="w-full h-full object-cover" />
                  <button
                    type="button"
                    onClick={() => removePendingPhoto(i)}
                    className="absolute top-0.5 right-0.5 w-5 h-5 rounded-full bg-rose-600 text-white text-[10px] font-bold"
                  >×</button>
                </div>
              ))}
            </div>
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              data-testid="vi-add-photo"
              className="w-full h-10 rounded-lg border-2 border-dashed border-slate-300 text-slate-500 text-xs font-semibold inline-flex items-center justify-center gap-2 hover:bg-slate-50"
            >
              <Camera size={14} /> Añadir foto
            </button>
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              capture="environment"
              multiple
              className="hidden"
              onChange={handlePhotoPick}
            />
          </div>
        </section>
      )}

      {critical && (
        <div
          className="rounded-xl bg-rose-700 text-white p-4 shadow-lg border-2 border-rose-900"
          data-testid="vi-critical-banner"
        >
          <div className="font-black text-sm uppercase tracking-wide flex items-start gap-2">
            <AlertTriangle size={20} className="shrink-0 mt-0.5" />
            <span>
              Incidencia crítica: NO UTILICES EL VEHÍCULO hasta recibir instrucciones del responsable o mecánico.
            </span>
          </div>
        </div>
      )}

      <button
        type="button"
        onClick={submit}
        disabled={!canSubmit}
        data-testid="vi-submit"
        className="brutal-btn-primary w-full inline-flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {saving ? <Loader2 className="animate-spin" size={18} /> : <Save size={18} />}
        Guardar revisión
      </button>
    </div>
  );
}


export function VehicleInspectionsList() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);
  const navigate = useNavigate();
  useEffect(() => {
    // Backend now decides scope based on the user's role or the
    // can_receive_vehicle_reports flag. No client-side gating.
    api.get("/vehicle-inspections")
      .then(({ data }) => setItems(data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const badgeCls = (s) =>
    s === "todo_correcto" ? "bg-emerald-100 text-emerald-800"
      : s === "incidencia" ? "bg-amber-100 text-amber-800"
      : "bg-rose-100 text-rose-800";
  const badgeLabel = (s) =>
    s === "todo_correcto" ? "Todo OK"
      : s === "incidencia" ? "Incidencia"
      : "No usar";

  return (
    <div className="space-y-5">
      <header className="flex items-start justify-between gap-2">
        <div>
          <div className="label-up text-[#FF4500]">Suntelco</div>
          <h1 className="font-display font-black text-3xl tracking-tight mt-1">Revisiones de vehículo</h1>
        </div>
        <button
          onClick={() => navigate("/vehicle-inspection/new")}
          data-testid="vi-new-btn"
          className="brutal-btn-primary h-11 px-4 text-sm inline-flex items-center gap-1.5"
        >
          <Truck size={14} /> Nueva
        </button>
      </header>
      {loading ? (
        <div className="text-center text-slate-500 py-10"><Loader2 className="animate-spin inline mr-2" size={18} /> Cargando…</div>
      ) : items.length === 0 ? (
        <div className="brutal-card p-8 text-center text-slate-500">
          Aún no hay revisiones registradas.
        </div>
      ) : (
        <ul className="space-y-2" data-testid="vi-list">
          {items.map((r) => (
            <li key={r.id}>
              <button
                type="button"
                onClick={() => setDetail(r)}
                data-testid={`vi-row-${r.id}`}
                className="w-full brutal-card p-3 flex items-center gap-3 text-left transition-all hover:-translate-y-[1px] hover:shadow-lg"
              >
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${
                  r.status === "todo_correcto" ? "bg-emerald-100 text-emerald-700"
                    : r.status === "incidencia" ? "bg-amber-100 text-amber-700"
                    : "bg-rose-100 text-rose-700"
                }`}>
                  {r.status === "todo_correcto" ? <CheckCircle2 size={20} /> : <AlertTriangle size={20} />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-sm truncate">
                    {r.matricula} · {r.kilometros?.toLocaleString?.("es-ES")} km
                  </div>
                  <div className="text-[11px] text-slate-500 truncate">
                    {formatVehicleDate(r.created_at)} · {r.user_name}
                  </div>
                </div>
                <span className={`text-[10px] font-bold uppercase px-2 py-1 rounded-full shrink-0 ${badgeCls(r.status)}`}>
                  {badgeLabel(r.status)}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
      {detail && <VehicleInspectionDetailModal item={detail} onClose={() => setDetail(null)} />}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Detail modal — full read-only view of a vehicle inspection report.
function VehicleInspectionDetailModal({ item, onClose }) {
  const badgeCls = item.status === "todo_correcto"
    ? "bg-emerald-100 text-emerald-800"
    : item.status === "incidencia" ? "bg-amber-100 text-amber-800"
    : "bg-rose-100 text-rose-800";
  const badgeLabel = item.status === "todo_correcto"
    ? "Todo correcto"
    : item.status === "incidencia" ? "Incidencia" : "No usar hasta revisión";

  // For inverted items (like "¿hay golpes?"), the meaning of SÍ/NO in the
  // detail modal is flipped so the reader sees the plain-language answer.
  const chk = [
    ["limpieza_ok", "Limpieza / orden", false],
    ["danos_ok", "¿Hay golpes o daños?", true],
    ["neumaticos_ok", "Neumáticos / aceite / agua", false],
    ["documentacion_ok", "Documentación / ITV / EPIs", false],
  ];
  const nivelLabel = {
    ninguna: "Ninguna",
    leve: "Leve",
    importante: "Importante",
    no_usar: "No usar",
  };
  const tipoLabel = {
    limpieza: "Limpieza / orden",
    golpe: "Golpe o daño",
    neumaticos: "Neumáticos / aceite / agua",
    documentacion: "Documentación / ITV / EPIs",
    averia: "Avería / testigo cuadro",
    otro: "Otro",
  };
  const circLabel = { si: "Sí", no: "No", no_seguro: "No estoy seguro" };

  return (
    <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-50 flex items-end sm:items-center justify-center p-0 sm:p-4"
      onClick={onClose} data-testid="vi-detail-modal">
      <div onClick={(e) => e.stopPropagation()}
        className="bg-white w-full sm:max-w-lg rounded-t-2xl sm:rounded-2xl shadow-xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-slate-200 flex items-center justify-between px-5 py-4">
          <div>
            <div className="label-up text-[#F5A623]">Revisión de vehículo</div>
            <div className="font-display font-black text-lg text-[#0B2A4A]">
              {item.matricula} · {item.kilometros?.toLocaleString?.("es-ES")} km
            </div>
          </div>
          <button type="button" onClick={onClose} data-testid="vi-detail-close" className="text-slate-500 hover:text-rose-600"><X size={20} /></button>
        </div>
        <div className="p-5 space-y-4">
          <div className="flex items-center justify-between text-xs text-slate-500">
            <span className="inline-flex items-center gap-1"><CalendarIcon size={12} /> {formatVehicleDate(item.created_at)}</span>
            <span className={`text-[10px] font-bold uppercase px-2 py-1 rounded-full ${badgeCls}`}>{badgeLabel}</span>
          </div>
          <div className="text-sm text-slate-700">
            <span className="text-slate-500">Operario:</span> <b className="text-[#0B2A4A]">{item.user_name}</b>
          </div>

          <div>
            <div className="label-up mb-2">Checklist</div>
            <ul className="space-y-1.5">
              {chk.map(([k, l, inverted]) => {
                // Stored value: item[k] === true → check passed (green).
                // For inverted items the plain-language answer is negated:
                //   passed=true (no damage) → answer "NO"
                //   passed=false (damage)   → answer "SÍ"
                const passed = item[k];
                const answerText = inverted ? (passed ? "NO" : "SÍ") : (passed ? "SÍ" : "NO");
                return (
                  <li key={k} className="flex items-center justify-between text-sm py-1 border-b border-slate-100 last:border-0">
                    <span className="text-slate-700">{l}</span>
                    <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded ${passed ? "bg-emerald-100 text-emerald-700" : "bg-rose-100 text-rose-700"}`}>
                      {answerText}
                    </span>
                  </li>
                );
              })}
            </ul>
          </div>

          <div>
            <div className="label-up mb-1">Nivel de incidencia</div>
            <div className="text-sm text-[#0B2A4A] font-semibold">{nivelLabel[item.incidencia_nivel] || item.incidencia_nivel}</div>
          </div>

          {item.incidencia_tipo && (
            <div>
              <div className="label-up mb-1">Tipo</div>
              <div className="text-sm">{tipoLabel[item.incidencia_tipo] || item.incidencia_tipo}</div>
            </div>
          )}

          {item.incidencia_desc && (
            <div>
              <div className="label-up mb-1">Descripción</div>
              <div className="text-sm whitespace-pre-wrap bg-slate-50 rounded-lg p-3 text-slate-700">{item.incidencia_desc}</div>
            </div>
          )}

          {item.puede_circular && (
            <div>
              <div className="label-up mb-1">¿Puede circular?</div>
              <div className="text-sm font-semibold text-[#0B2A4A]">{circLabel[item.puede_circular] || item.puede_circular}</div>
            </div>
          )}

          {Array.isArray(item.photo_urls) && item.photo_urls.length > 0 && (
            <div>
              <div className="label-up mb-2">Fotos</div>
              <div className="grid grid-cols-3 gap-2">
                {item.photo_urls.map((u, i) => (
                  <a key={i} href={u} target="_blank" rel="noreferrer" className="block aspect-square rounded-lg overflow-hidden bg-slate-100 border border-slate-200">
                    <img src={u} alt={`foto-${i + 1}`} className="w-full h-full object-cover" />
                  </a>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// Robust date formatter: server returns ISO-8601. Fallback to em-dash if parse fails.
function formatVehicleDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString("es-ES", {
    day: "2-digit", month: "2-digit", year: "numeric",
    hour: "2-digit", minute: "2-digit",
  });
}
